import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { parseLocalDate, toDateInputValue } from '../../../utils/dateUtils';

const AddDeliverableModal = ({ isOpen, onClose, onSave, editingDeliverable, phaseId, phase, availableResources }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    estimatedHours: '',
    priority: 'medium',
    status: 'pending',
    assignedResources: [],
    resourceDates: {}
  });

  const [errors, setErrors] = useState({});
  const [showResourcePicker, setShowResourcePicker] = useState(false);
  const [resourceFilter, setResourceFilter] = useState('');
  const [resourceCategoryFilter, setResourceCategoryFilter] = useState('all');
  const [resourceStatusFilter, setResourceStatusFilter] = useState('all');
  const [resourceSortBy, setResourceSortBy] = useState('name');
  const [resourceSortDir, setResourceSortDir] = useState('asc');

  useEffect(() => {
    if (editingDeliverable) {
      const resourceDatesMap = {};
      (editingDeliverable?.assignedResources || [])?.forEach(r => {
        if (r?.id) {
          resourceDatesMap[r.id] = {
            startDate: r?.allocationStartDate || editingDeliverable?.startDate || '',
            endDate: r?.allocationEndDate || editingDeliverable?.endDate || ''
          };
        }
      });
      setFormData({
        name: editingDeliverable?.name,
        description: editingDeliverable?.description,
        startDate: editingDeliverable?.startDate ? toDateInputValue(editingDeliverable?.startDate) : '',
        endDate: editingDeliverable?.endDate ? toDateInputValue(editingDeliverable?.endDate) : '',
        estimatedHours: editingDeliverable?.estimatedHours?.toString(),
        priority: editingDeliverable?.priority,
        status: editingDeliverable?.status,
        assignedResources: (editingDeliverable?.assignedResources || [])?.map(r => r?.id),
        resourceDates: resourceDatesMap
      });
    } else {
      setFormData({
        name: '',
        description: '',
        startDate: phase?.startDate || '',
        endDate: phase?.endDate || '',
        estimatedHours: '',
        priority: 'medium',
        status: 'pending',
        assignedResources: [],
        resourceDates: {}
      });
    }
    setErrors({});
  }, [editingDeliverable, isOpen, phase]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Deliverable name is required';
    }

    if (!formData?.startDate) {
      newErrors.startDate = 'Start date is required';
    }

    if (!formData?.endDate) {
      newErrors.endDate = 'End date is required';
    }

    if (formData?.startDate && formData?.endDate && parseLocalDate(formData?.startDate) > parseLocalDate(formData?.endDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    if (!formData?.estimatedHours || parseFloat(formData?.estimatedHours) <= 0) {
      newErrors.estimatedHours = 'Estimated hours must be greater than 0';
    }

    if (formData?.assignedResources?.length === 0) {
      newErrors.assignedResources = 'At least one resource must be assigned';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      onSave({
        ...formData,
        id: editingDeliverable?.id,
        phaseId,
        estimatedHours: parseFloat(formData?.estimatedHours),
        actualHours: editingDeliverable?.actualHours || 0
      });
      if (!editingDeliverable) {
        onClose();
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors?.[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const toggleResource = (resourceId) => {
    setFormData(prev => {
      const isSelected = prev?.assignedResources?.includes(resourceId);
      if (isSelected) {
        const newDates = { ...prev?.resourceDates };
        delete newDates?.[resourceId];
        return { ...prev, assignedResources: prev?.assignedResources?.filter(id => id !== resourceId), resourceDates: newDates };
      } else {
        return {
          ...prev,
          assignedResources: [...prev?.assignedResources, resourceId],
          resourceDates: {
            ...prev?.resourceDates,
            [resourceId]: { startDate: prev?.startDate || '', endDate: prev?.endDate || '' }
          }
        };
      }
    });

    if (errors?.assignedResources) {
      setErrors(prev => ({ ...prev, assignedResources: '' }));
    }
  };

  const handleResourceDateChange = (resourceId, field, value) => {
    setFormData(prev => ({
      ...prev,
      resourceDates: {
        ...prev?.resourceDates,
        [resourceId]: { ...prev?.resourceDates?.[resourceId], [field]: value }
      }
    }));
  };

  const resourceCategories = [...new Set(availableResources?.map(r => r?.category)?.filter(Boolean))];
  const resourceStatuses = [...new Set(availableResources?.map(r => r?.status)?.filter(Boolean))];

  const toggleSort = (field) => {
    if (resourceSortBy === field) {
      setResourceSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setResourceSortBy(field);
      setResourceSortDir('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (resourceSortBy !== field) return <Icon name="ChevronsUpDown" size={12} className="text-muted-foreground" />;
    return resourceSortDir === 'asc'
      ? <Icon name="ChevronUp" size={12} className="text-accent" />
      : <Icon name="ChevronDown" size={12} className="text-accent" />;
  };

  const filteredResources = availableResources
    ?.filter(resource =>
      (resource?.name?.toLowerCase()?.includes(resourceFilter?.toLowerCase()) ||
        resource?.category?.toLowerCase()?.includes(resourceFilter?.toLowerCase())) &&
      (resourceCategoryFilter === 'all' || resource?.category === resourceCategoryFilter) &&
      (resourceStatusFilter === 'all' || resource?.status?.toLowerCase() === resourceStatusFilter?.toLowerCase())
    )
    ?.sort((a, b) => {
      let valA = a?.[resourceSortBy] || '';
      let valB = b?.[resourceSortBy] || '';
      if (typeof valA === 'string') valA = valA?.toLowerCase();
      if (typeof valB === 'string') valB = valB?.toLowerCase();
      if (valA < valB) return resourceSortDir === 'asc' ? -1 : 1;
      if (valA > valB) return resourceSortDir === 'asc' ? 1 : -1;
      return 0;
    });

  // Get selected resource objects for display
  const selectedResourceObjects = availableResources?.filter(r => 
    formData?.assignedResources?.includes(r?.id)
  );

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50">
      <div className="bg-card rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-semibold text-foreground">
            {editingDeliverable ? 'Edit Deliverable' : 'Add New Deliverable'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
            aria-label="Close modal"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <Input
            label="Deliverable Name"
            type="text"
            name="name"
            value={formData?.name}
            onChange={handleChange}
            placeholder="e.g., Pour Foundation Concrete"
            error={errors?.name}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <textarea
              name="description"
              value={formData?.description}
              onChange={handleChange}
              placeholder="Describe the deliverable details and requirements..."
              rows={3}
              className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring resize-none"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Start Date"
              type="date"
              name="startDate"
              value={formData?.startDate}
              onChange={handleChange}
              error={errors?.startDate}
              required
            />

            <Input
              label="End Date"
              type="date"
              name="endDate"
              value={formData?.endDate}
              onChange={handleChange}
              error={errors?.endDate}
              required
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input
              label="Estimated Hours"
              type="number"
              name="estimatedHours"
              value={formData?.estimatedHours}
              onChange={handleChange}
              placeholder="0"
              error={errors?.estimatedHours}
              required
              min="0"
              step="0.5"
            />

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Priority
              </label>
              <select
                name="priority"
                value={formData?.priority}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Status
              </label>
              <select
                name="status"
                value={formData?.status}
                onChange={handleChange}
                className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              >
                <option value="pending">Pending</option>
                <option value="in-progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Assigned Resources *
            </label>
            
            {selectedResourceObjects?.length > 0 && (
              <div className="flex flex-col gap-2 mb-3">
                {selectedResourceObjects?.map((resource) => (
                  <div
                    key={resource?.id}
                    className="border border-border rounded-lg p-3 bg-muted/50"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 rounded-full overflow-hidden bg-background flex-shrink-0">
                        {resource?.imageUrl ? (
                          <Image
                            src={resource?.imageUrl}
                            alt={resource?.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground text-xs font-medium">
                            {resource?.name?.charAt(0)}
                          </div>
                        )}
                      </div>
                      <span className="text-sm font-medium text-foreground flex-1">{resource?.name}</span>
                      <button
                        type="button"
                        onClick={() => toggleResource(resource?.id)}
                        className="p-1 hover:bg-background rounded transition-colors"
                      >
                        <Icon name="X" size={14} />
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">Start Date</label>
                        <input
                          type="date"
                          value={formData?.resourceDates?.[resource?.id]?.startDate || ''}
                          onChange={(e) => handleResourceDateChange(resource?.id, 'startDate', e?.target?.value)}
                          className="w-full px-2 py-1.5 bg-input border border-border rounded text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-muted-foreground mb-1">End Date</label>
                        <input
                          type="date"
                          value={formData?.resourceDates?.[resource?.id]?.endDate || ''}
                          onChange={(e) => handleResourceDateChange(resource?.id, 'endDate', e?.target?.value)}
                          className="w-full px-2 py-1.5 bg-input border border-border rounded text-xs text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={() => setShowResourcePicker(!showResourcePicker)}
              className="w-full px-4 py-3 bg-input border border-border rounded-lg text-left text-muted-foreground hover:border-ring transition-colors flex items-center justify-between"
            >
              <span>Select resources...</span>
              <Icon name={showResourcePicker ? 'ChevronUp' : 'ChevronDown'} size={20} />
            </button>

            {errors?.assignedResources && (
              <p className="text-sm text-red-600 mt-1">{errors?.assignedResources}</p>
            )}

            {showResourcePicker && (
              <div className="mt-2 border border-border rounded-lg overflow-hidden">
                <div className="p-3 border-b border-border space-y-2">
                  <Input
                    type="text"
                    placeholder="Search by name or category..."
                    value={resourceFilter}
                    onChange={(e) => setResourceFilter(e?.target?.value)}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <select
                      value={resourceCategoryFilter}
                      onChange={(e) => setResourceCategoryFilter(e?.target?.value)}
                      className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="all">All Categories</option>
                      {resourceCategories?.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <select
                      value={resourceStatusFilter}
                      onChange={(e) => setResourceStatusFilter(e?.target?.value)}
                      className="w-full px-3 py-2 bg-input border border-border rounded-lg text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
                    >
                      <option value="all">All Statuses</option>
                      {resourceStatuses?.map(s => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">Sort by:</span>
                    {['name', 'category', 'status']?.map(field => (
                      <button
                        key={field}
                        type="button"
                        onClick={() => toggleSort(field)}
                        className={`inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-medium transition-colors ${
                          resourceSortBy === field
                            ? 'bg-accent/10 text-accent border border-accent/30' :'bg-muted text-muted-foreground hover:bg-muted/80'
                        }`}
                      >
                        {field?.charAt(0)?.toUpperCase() + field?.slice(1)}
                        <SortIcon field={field} />
                      </button>
                    ))}
                    <span className="ml-auto text-xs text-muted-foreground">{filteredResources?.length} result{filteredResources?.length !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                <div className="max-h-64 overflow-y-auto">
                  {filteredResources?.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">No resources match the filters</p>
                  ) : (
                    filteredResources?.map((resource) => {
                      const isSelected = formData?.assignedResources?.includes(resource?.id);
                      
                      return (
                        <button
                          key={resource?.id}
                          type="button"
                          onClick={() => toggleResource(resource?.id)}
                          className={`w-full flex items-center gap-3 p-3 hover:bg-muted transition-colors ${
                            isSelected ? 'bg-accent/10' : ''
                          }`}
                        >
                          <div className="w-10 h-10 rounded-full overflow-hidden bg-background flex-shrink-0">
                            {resource?.imageUrl ? (
                              <Image
                                src={resource?.imageUrl}
                                alt={resource?.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center bg-primary text-primary-foreground font-medium">
                                {resource?.name?.charAt(0)}
                              </div>
                            )}
                          </div>
                          <div className="flex-1 text-left min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">
                              {resource?.name}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {resource?.category}
                            </p>
                          </div>
                          {resource?.status && (
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                              resource?.status?.toLowerCase() === 'available' ?'bg-green-100 text-green-700'
                                : resource?.status?.toLowerCase() === 'in use' || resource?.status?.toLowerCase() === 'in-use'
                                ? 'bg-amber-100 text-amber-700' :'bg-muted text-muted-foreground'
                            }`}>{resource?.status}</span>
                          )}
                          {isSelected && (
                            <Icon name="Check" size={20} color="var(--color-accent)" />
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="default"
              iconName="Save"
              iconPosition="left"
            >
              {editingDeliverable ? 'Update Deliverable' : 'Create Deliverable'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddDeliverableModal;